"use client";

import React, { useState } from "react";
import Image from "next/image";
import { motion } from "framer-motion";

export type Formation = { name: string; days: string; city: string };

type Props = {
  firstName: string;
  onLogout: () => void;
};

export default function WelcomeDashboard({ firstName, onLogout }: Props) {
  const [activeTab, setActiveTab] = useState<"formations" | "certificats" | "financement">("formations");
  const [selectedFormation, setSelectedFormation] = useState<null | Formation>(null);

  const tabs = [
    { id: "formations", label: "Mes Formations" },
    { id: "certificats", label: "Mes Certificats" },
    { id: "financement", label: "Mes aides de financement" },
  ] as const;

  // TODO: placeholder rows — this dashboard is not wired to the API yet, so the
  // three formations, their documents and the financing tab are all hardcoded.
  const formations: Formation[] = [
    { name: "Marketing Digital", days: "3 jours", city: "Paris" },
    { name: "Gestion de projet", days: "3 jours", city: "Paris" },
    { name: "SEO", days: "3 jours", city: "Paris" },
  ];

  return (
    <div className="flex flex-col h-full">
      <div className="login-responsive px-6 pt-4 text-center">
        <h2 className="text-base leading-6.5 tracking-[2%] font-bold text-[#FC7F16] mx-auto text-center">
          Bienvenue {firstName}
        </h2>
      </div>

      {/* Tabs */}
      <div className="md:px-4 pt-4 px-3 flex items-center">
        <div className="mx-auto bg-white rounded-full flex relative md:gap-1.5 overflow-hidden">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              type="button"
              onClick={() => setActiveTab(tab.id)}
              className={`
                relative z-10 cursor-pointer px-5 sm:px-7 py-1.75 rounded-full text-[8px] leading-5 font-normal transition-colors duration-300
                ${activeTab === tab.id ? "text-white" : "text-[#1B365E] hover:text-[#1B365E]"}
              `}
            >
              {tab.label}
              {activeTab === tab.id && (
                <motion.div
                  layoutId="active-dashboard-tab"
                  className="absolute inset-0 bg-[#1B365E] rounded-full -z-10"
                  transition={{ type: "spring", bounce: 0.2, duration: 0.6 }}
                />
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="md:px-4 px-3 pt-4 pb-6 space-y-4 overflow-auto">
        {activeTab === "formations" && (
          !selectedFormation ? (
            <>
              <p className="text-[#1D1D1D] leading-5 tracking-[3%] text-[8px]">3 Formations en cours</p>

              <ul className="space-y-4">
                {formations.map((item) => (
                  <li
                    key={item.name}
                    className="group cursor-pointer flex items-center justify-between gap-4"
                    onClick={() => setSelectedFormation(item)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") setSelectedFormation(item);
                    }}
                  >
                    <span className="flex-1 group-hover:underline text-[12px] tracking-[2%] text-[#1D1D1D]">{item.name}</span>
                    <span className="text-[#1B365E] text-[8px] w-9.25">{item.days}</span>
                    <span className="text-[#1B365E] text-[8px] w-10.25">{item.city}</span>
                    <Image className="w-4.5 h-4" src="/assets/icons/folder-open-solid.svg" alt="Folder Open" title='Folder Open' width={18} height={16} />
                  </li>
                ))}
              </ul>

              <p className="border-y border-[#FFFFFF]/30 py-4 text-[#1D1D1D] leading-5 tracking-[3%] text-[8px]">0 Formations terminées</p>

              <div className="text-center h-5 flex items-center justify-center">
                <button onClick={onLogout} className="cursor-pointer text-[8px] text-[#1B365E] leading-5 tracking-[3%] underline hover:text-[#0f2442]">
                  Se déconnecter
                </button>
              </div>
            </>
          ) : (
            <>
              {/* Back link */}
              <button
                type="button"
                className="cursor-pointer hover:underline hover:text-[#0f2442] flex items-center gap-2 text-[#1B365E] text-[8px] leading-5"
                onClick={() => setSelectedFormation(null)}
              >
                <Image src="/assets/icons/back-arrow.svg" alt="Retour" title="Retour" width={16} height={16} />
                Retour à Mes Formations
              </button>

              <div className="relative w-full max-w-[1100px] bg-white rounded-[16px] px-10 pb-4 pt-6.25 mb-6">

                {/* Top Center Pill */}
                <div className="absolute md:-top-4 -top-2.5 left-1/2 -translate-x-1/2 bg-white md:px-4 px-2 md:py-2 py-1 rounded-full text-[#1B515E] md:text-sm text-[8px]">
                  Marketing et Digital
                </div>

                {/* Top Right Text */}
                <div className="absolute md:top-5 top-[11.18px] md:right-6 right-[14.16px] text-[#1B365E] md:text-sm text-[8px]">
                  3 jours à Paris
                </div>

                {/* Center Content */}
                <div className="flex flex-col items-center justify-center gap-[8.27px]">
                  {/* Illustration */}
                  <div className="relative">
                    <Image className="w-[66.61px] h-[81.55px]" src="/assets/marketing-digital.png" alt="Marketing Digital" title="Marketing Digital" width={66.61} height={81.55} />
                  </div>

                  {/* Title */}
                  <h2 className="md:text-[20px] text-[15px] font-bold text-[#1E1E1E]">
                    Marketing Digital
                  </h2>
                </div>
              </div>

              {/* Tag */}
              <div className="xl:inline-block login-responsive w-fit bg-white text-[#1B365E] text-[8px] px-2 py-1 rounded-full">
                Marketing et Digital
              </div>

              {/* Main info - Title + Duration + City */}
              <div className="login-responsive-grid grid grid-cols-12 md:gap-x-4 gap-x-2 items-start">
                <h3 className="col-span-8 text-[16px] md:text-[17px] font-bold leading-tight">
                  Marketing Digital
                </h3>

                <div className="col-span-2 text-right text-[8px] text-[#1D1D1D] leading-5 whitespace-nowrap">3 jours</div>
                <div className="col-span-2 text-[8px] text-[#1D1D1D] leading-5 whitespace-nowrap">à Paris</div>
              </div>

              {/* Status + Dates */}
              <div className="grid grid-cols-12 md:gap-x-4 gap-x-2 items-center">
                <div className="col-span-8 flex items-center gap-[8px]">
                  <div className="w-[18px] h-[18px] rounded-full bg-[#B6C93E] border border-white flex-shrink-0" />
                  <span className="text-[12px] font-medium">En cours</span>
                </div>

                <div className="col-span-2 text-[#1D1D1D] text-[8px] text-right">Du 12/05/2025</div>
                <div className="col-span-2 text-[#1D1D1D] text-[8px]">Au 14/05/2025</div>
              </div>

              {/* Télécharger le programme */}
              <div className="border-b border-[#FFFFFF]/25 pb-4">
                <div className="grid grid-cols-12 md:gap-x-4 gap-x-2 items-center">
                  <div className="col-span-8 flex items-center md:gap-4 gap-2 text-[#1B365E]">
                    <Image className="w-4 h-4" src="/assets/icons/arrow-down-file.svg" alt="arrow-down" title="arrow-down" width={18} height={18} />
                    <span className="text-[12px]">Téléchargez le programme</span>
                  </div>

                  <span className="col-span-2 text-[#1B365E] text-[8px] text-right">128Mb</span>
                  <span className="col-span-2 text-[#1B365E] text-[8px]">6 Modules</span>
                </div>
              </div>

              {/* Documents */}
              <div>
                <div className="flex items-center gap-3.5 mb-4">
                  <Image className="w-[18px] h-[14px]" src="/assets/icons/folder-open-solid-cyan.svg" alt="Dossier" title="Dossier" width={20} height={18} />
                  <h3 className="text-[14px]">Vos documents à disposition :</h3>
                </div>

                <div className="space-y-4">
                  {[
                    { name: "Programme de formation", size: "1,2 Mo" },
                    { name: "Convention de formation", size: "340 Ko" },
                    { name: "Attestation de présence", size: "180 Ko" },
                  ].map((doc, i) => (
                    <div
                      key={i}
                      className="grid grid-cols-12 md:gap-x-4 gap-x-2 items-center"
                    >
                      {/* Left: icon + name */}
                      <div className="col-span-8 flex items-center md:gap-4 gap-2">
                        <Image
                          src="/assets/icons/arrow-down-file.svg"
                          alt="Document"
                          width={18}
                          height={18}
                          className="w-4 h-4"
                        />
                        <span className="text-[#1B365E] text-[12px]">
                          {doc.name}
                        </span>
                      </div>

                      {/* Right: size */}
                      <span className="col-span-2 text-[#1B365E] text-[8px] text-right">
                        {doc.size}
                      </span>

                      {/* Spacer column (1fr) */}
                      <div className="col-span-2" />
                    </div>
                  ))}

                </div>
              </div>

              {/* Footer note */}
              <p className="border-y border-[#FFFFFF]/30 py-4 text-[#1D1D1D] leading-5 tracking-[3%] text-[8px] xl:text-left text-center"><span className="font-bold">0</span> Résultats pour cette formation. <br className="md:hidden block" /> Vous pouvez voir vos résultats et vos diplômes une fois la formation terminée.</p>

              <div className="text-center h-5 flex items-center justify-center mt-4">
                <button onClick={onLogout} className="cursor-pointer text-[8px] text-[#1B365E] leading-5 tracking-[3%] underline hover:text-[#0f2442]">
                  Se déconnecter
                </button>
              </div>
            </>
          )
        )}

        {activeTab !== "formations" && (
          <div className="text-center text-[10px] text-[#1B365E] opacity-70 py-10">
            Contenu non disponible dans cette maquette.
          </div>
        )}
      </div>
    </div>
  );
}
